import { BAND_COUNT, MAX_COLUMNS, MIN_COLUMNS } from '@hmawiw/shared';
import { useEffect, useState, type CSSProperties } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useOrderedBoards } from '../../hooks/useBoards';
import { useNow } from '../../hooks/useMedia';
import { defaultBandLabel, defaultColumnName, translate, useT, type TKey } from '../../i18n';
import { formatRelative } from '../../lib/device';
import { installedApp, type InstalledApp } from '../../native/appInfo';
import { apiBase, isNative } from '../../native/platform';
import { ofBoard, openCards, useCards } from '../../store/cards';
import { useSession } from '../../store/session';
import { useUi } from '../../store/ui';
import { ACCENTS, useSettings, type BandPreset, type Locale, type Theme, type TimeFormat } from '../../store/settings';
import { api, type DeviceInfo } from '../../sync/api';
import { flush, useSync } from '../../sync/engine';
import { useTour } from '../../tour/store';
import { usePwaInstall } from '../../install/pwa';
import { pushSupported, usePush } from '../../push/web';
import { useInstall } from '../../update/install';
import controls from '../controls.module.css';
import { ChevronDown, ChevronLeft, ChevronUp, Cross } from '../icons';
import styles from './Settings.module.css';

const PRESETS: BandPreset[] = ['importance', 'horizon'];
const THEMES: Theme[] = ['system', 'light', 'dark'];
const TIME_FORMATS: TimeFormat[] = ['auto', '12h', '24h'];

export function Settings() {
  const t = useT();
  const s = useSettings();
  const session = useSession();
  const navigate = useNavigate();
  const startTour = useTour((st) => st.start);
  const location = useLocation();
  useEffect(() => {
    if (location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [location.hash]);
  // The count changes and every card keeps its place on the new scale before the setting lands.
  const changeColumns = async (n: number) => {
    if (n < MIN_COLUMNS || n > MAX_COLUMNS || n === s.columnCount) return;
    await useCards.getState().remapColumns(s.columnCount, n);
    await s.set('columnCount', n);
    await s.set('columnLabels', Array.from({ length: n }, (_, i) => s.columnLabels[i] ?? null));
  };

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <Link to="/" className={controls.iconbtn} aria-label={t.t('nav.back')}>
          <ChevronLeft size={24} />
        </Link>
        <h1 className={`display ${styles.title}`}>{t.t('settings.title')}</h1>
      </header>
      <main className={styles.main}>
        <AccountSection />

        <BoardsSection />

        <section className={styles.group}>
          <div className={controls.label}>{t.t('settings.board')}</div>
          <div className={styles.card}>
            <div className={styles.row}>
              <span className={styles.rowTitle}>{t.t('settings.bandPreset')}</span>
              <div className={controls.segs}>
                {PRESETS.map((p) => (
                  <button key={p} type="button" className={controls.seg} aria-pressed={s.bandPreset === p} onClick={() => void s.set('bandPreset', p)}>
                    {t.t(`settings.bandPreset.${p}` as TKey)}
                  </button>
                ))}
              </div>
            </div>
            <p className={styles.hint}>{t.t('settings.bandPresetHint')}</p>
            <div className={styles.row}>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span className={styles.rowTitle}>{t.t('settings.columns')}</span>
                <span className={styles.hint} style={{ margin: 0 }}>
                  {t.t('settings.columnsHint')}
                </span>
              </div>
              <div className={controls.segs} role="group" aria-label={t.t('settings.columns')}>
                <button type="button" className={controls.seg} disabled={s.columnCount <= MIN_COLUMNS} onClick={() => void changeColumns(s.columnCount - 1)} aria-label={t.t('settings.fewerColumns')}>
                  −
                </button>
                <span className={`${controls.seg} ${styles.count}`} aria-live="polite" data-columns={s.columnCount}>
                  {s.columnCount}
                </span>
                <button type="button" className={controls.seg} disabled={s.columnCount >= MAX_COLUMNS} onClick={() => void changeColumns(s.columnCount + 1)} aria-label={t.t('settings.moreColumns')}>
                  +
                </button>
              </div>
            </div>
            <LabelEditor
              title={t.t('settings.columnNames')}
              count={s.columnCount}
              values={s.columnLabels}
              placeholder={(i) => (s.columnCount === 3 ? session.defaults.columnLabels[i] : null) || defaultColumnName(t.locale, i, s.columnCount)}
              colour={(i) => `var(--m${i})`}
              onChange={(next) => void s.set('columnLabels', next)}
            />
            <LabelEditor
              title={t.t('settings.bandNames')}
              count={BAND_COUNT}
              values={s.bandLabels}
              placeholder={(i) => session.defaults.bandLabels[i] || defaultBandLabel(t.locale, s.bandPreset, i)}
              onChange={(next) => void s.set('bandLabels', next)}
            />
            <button
              type="button"
              className={styles.linkbtn}
              onClick={() => {
                void s.set('columnLabels', Array.from({ length: s.columnCount }, () => null));
                void s.set('bandLabels', Array.from({ length: BAND_COUNT }, () => null));
              }}
            >
              {t.t('settings.resetLabels')}
            </button>
          </div>
        </section>

        <section className={styles.group}>
          <div className={controls.label}>{t.t('settings.preferences')}</div>
          <div className={styles.card}>
            <div className={styles.row}>
              <span className={styles.rowTitle}>{t.t('settings.language')}</span>
              <select className={styles.select} value={s.locale ?? ''} onChange={(e) => void s.set('locale', (e.target.value || null) as Locale | null)} aria-label={t.t('settings.language')}>
                <option value="">{t.t('settings.language.auto')}</option>
                <option value="en">English</option>
                <option value="fr">Français</option>
              </select>
            </div>
            <div className={styles.row}>
              <span className={styles.rowTitle}>{t.t('settings.timeFormat')}</span>
              <div className={controls.segs}>
                {TIME_FORMATS.map((f) => (
                  <button key={f} type="button" className={controls.seg} aria-pressed={s.timeFormat === f} onClick={() => void s.set('timeFormat', f)}>
                    {t.t(`settings.timeFormat.${f}` as TKey)}
                  </button>
                ))}
              </div>
            </div>
            <div className={styles.row}>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span className={styles.rowTitle}>{t.t('settings.timerAlert')}</span>
                <span className={styles.hint} style={{ margin: 0 }}>
                  {t.t('settings.timerAlertHint')}
                </span>
              </div>
              <div className={controls.segs}>
                <button type="button" className={controls.seg} aria-pressed={!s.timerRing} onClick={() => void s.set('timerRing', false)}>
                  {t.t('settings.alert.notify')}
                </button>
                <button type="button" className={controls.seg} aria-pressed={s.timerRing} onClick={() => void s.set('timerRing', true)}>
                  {t.t('settings.alert.ring')}
                </button>
              </div>
            </div>
            {isNative ? (
              <div className={styles.row}>
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <span className={styles.rowTitle}>{t.t('settings.todayShade')}</span>
                  <span className={styles.hint} style={{ margin: 0 }}>
                    {t.t('settings.todayShadeHint')}
                  </span>
                </div>
                <div className={controls.segs}>
                  <button type="button" className={controls.seg} aria-pressed={!s.todayShade} onClick={() => s.setTodayShade(false)}>
                    {t.t('settings.off')}
                  </button>
                  <button type="button" className={controls.seg} aria-pressed={s.todayShade} onClick={() => s.setTodayShade(true)}>
                    {t.t('settings.on')}
                  </button>
                </div>
              </div>
            ) : null}
            {isNative ? null : <InstallRow />}
            {isNative ? null : <PushRow />}
            <div className={styles.row}>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span className={styles.rowTitle}>{t.t('settings.checkin')}</span>
                <span className={styles.hint} style={{ margin: 0 }}>
                  {t.t('settings.checkinHint')}
                </span>
              </div>
              <input type="time" className={styles.select} value={s.checkinTime ?? ''} onChange={(e) => void s.set('checkinTime', e.target.value || null)} aria-label={t.t('settings.checkin')} />
            </div>
            <div className={styles.row}>
              <span className={styles.rowTitle}>{t.t('settings.theme')}</span>
              <div className={controls.segs}>
                {THEMES.map((theme) => (
                  <button key={theme} type="button" className={controls.seg} aria-pressed={s.theme === theme} onClick={() => s.setTheme(theme)}>
                    {t.t(`settings.theme.${theme}` as TKey)}
                  </button>
                ))}
              </div>
            </div>
            <div className={styles.row}>
              <span className={styles.rowTitle}>{t.t('settings.tour')}</span>
              <button
                type="button"
                className={`${controls.btn} ${controls.quiet}`}
                onClick={() => {
                  void startTour().then(() => navigate('/'));
                }}
              >
                {t.t('settings.tourStart')}
              </button>
            </div>
            <div className={`${styles.row} ${styles.accentRow}`}>
              <span className={styles.rowTitle}>{t.t('settings.accent')}</span>
              <div className={styles.swatches} role="radiogroup" aria-label={t.t('settings.accent')}>
                {ACCENTS.map((a) => (
                  <button
                    key={a}
                    type="button"
                    role="radio"
                    aria-checked={s.accent === a}
                    aria-label={t.t(`accent.${a}` as TKey)}
                    title={t.t(`accent.${a}` as TKey)}
                    className={styles.swatch}
                    style={{ '--swatch': `var(--accent-${a})` } as CSSProperties}
                    onClick={() => void s.set('accent', a)}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        <DevicesSection />
        <AboutSection />
      </main>
    </div>
  );
}

/** Web only: the browser's install offer when it makes one, the menu hint otherwise. */
function InstallRow() {
  const t = useT();
  const installed = usePwaInstall((i) => i.installed);
  const canPrompt = usePwaInstall((i) => i.canPrompt);
  const prompt = usePwaInstall((i) => i.prompt);
  return (
    <div className={styles.row}>
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <span className={styles.rowTitle}>{t.t('install.title')}</span>
        <span className={styles.hint} style={{ margin: 0 }}>
          {installed ? t.t('install.installed') : canPrompt ? t.t('install.hint') : t.t('install.manual')}
        </span>
      </div>
      {!installed && canPrompt ? (
        <button type="button" className={`${controls.btn} ${controls.primary}`} onClick={() => void prompt()}>
          {t.t('install.button')}
        </button>
      ) : null}
    </div>
  );
}

/** Web only: the browser's own push subscription, per browser, opt in. */
function PushRow() {
  const t = useT();
  const status = usePush((p) => p.status);
  const busy = usePush((p) => p.busy);
  const error = usePush((p) => p.error);
  const enable = usePush((p) => p.enable);
  const disable = usePush((p) => p.disable);
  const test = usePush((p) => p.test);
  const showToast = useUi((u) => u.showToast);
  if (!pushSupported()) {
    return (
      <div className={styles.row}>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span className={styles.rowTitle}>{t.t('push.title')}</span>
          <span className={styles.hint} style={{ margin: 0 }}>
            {t.t('push.unsupported')}
          </span>
        </div>
      </div>
    );
  }
  return (
    <>
      <div className={styles.row}>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span className={styles.rowTitle}>{t.t('push.title')}</span>
          <span className={styles.hint} style={{ margin: 0 }}>
            {status === 'denied' ? t.t('push.denied') : error ? t.t('push.error', { message: error }) : t.t('push.hint')}
          </span>
        </div>
        <label className={styles.switchLabel}>
          <input type="checkbox" className={styles.check} checked={status === 'on'} disabled={busy || status === 'denied'} onChange={(e) => void (e.target.checked ? enable() : disable())} aria-label={t.t('push.title')} />
        </label>
      </div>
      {status === 'on' ? (
        <div className={styles.row}>
          <span className={styles.rowTitle}>{t.t('push.testTitle')}</span>
          <button
            type="button"
            className={`${controls.btn} ${controls.quiet}`}
            onClick={() => {
              void test().then((ok) => showToast(t.t(ok ? 'push.testSent' : 'push.testFailed')));
            }}
          >
            {t.t('push.test')}
          </button>
        </div>
      ) : null}
    </>
  );
}

function AboutSection() {
  const t = useT();
  const meta = useSession((s) => s.meta);
  const refreshMeta = useSession((s) => s.refreshMeta);
  const cards = useCards((c) => c.cards);
  const liveCount = Object.values(cards).filter((c) => c.deletedAt === null).length;
  const [installed, setInstalled] = useState<InstalledApp | null>(null);

  useEffect(() => {
    void installedApp().then(setInstalled);
    // A newly published APK should show up here without restarting the app.
    void refreshMeta();
  }, [refreshMeta]);

  const apk = meta?.apk ?? null;
  const newer = !!apk && !!installed && apk.versionCode !== null && apk.versionCode > installed.versionCode;
  const apkTitle = newer ? 'settings.apkUpdate' : installed ? 'settings.apkCurrent' : 'settings.apk';
  const phase = useInstall((s) => s.phase);
  const progress = useInstall((s) => s.progress);
  const start = useInstall((s) => s.start);
  const apkUrl = `${apiBase()}/downloads/hmawiw.apk`;
  const apkHint = apk
    ? phase === 'downloading' && newer ? t.t('update.downloading', { pct: Math.round(progress * 100) })
    : phase === 'installing' && newer ? t.t('update.installing')
    : phase === 'permission' && newer ? t.t('update.allowInstall')
    : phase === 'error' && newer ? t.t('update.failed')
    : t.t('settings.apkHint', { version: apk.versionName ?? '?', code: apk.versionCode ?? '?', build: apk.commit ?? '?', size: (apk.size / 1_048_576).toFixed(1) })
    : '';

  return (
    <section className={styles.group}>
      <div className={controls.label}>{t.t('settings.about')}</div>
      <div className={styles.card}>
        <div className={styles.row}>
          <span className={styles.rowTitle}>{t.t('app.name')}</span>
          <span className={styles.value}>
            {installed ? t.t('settings.versionCode', { version: installed.versionName, code: installed.versionCode }) : t.t('settings.version', { version: __APP_VERSION__ })}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.rowTitle}>{t.t('settings.build')}</span>
          <span className={`mono ${styles.value}`} data-build={__BUILD_ID__}>
            {__BUILD_ID__} · {__BUILD_TIME__}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.rowTitle}>{t.t('settings.storage', { n: liveCount })}</span>
        </div>
        {apk && installed ? (
          // Inside the Android shell the update downloads in the app and goes straight to the installer.
          newer ? (
            <button type="button" className={styles.row} style={{ width: '100%', textAlign: 'left' }} disabled={phase === 'downloading' || phase === 'installing'} onClick={() => void start(apkUrl)}>
              <span className={styles.rowTitle} style={{ color: 'var(--accent)', fontWeight: 600 }}>
                {t.t(apkTitle)}
              </span>
              <span className={styles.value}>{apkHint}</span>
            </button>
          ) : (
            <div className={styles.row}>
              <span className={styles.rowTitle}>{t.t(apkTitle)}</span>
              <span className={styles.value}>{apkHint}</span>
            </div>
          )
        ) : apk ? (
          <a className={styles.row} href={apkUrl} style={{ textDecoration: 'none', color: 'inherit' }}>
            <span className={styles.rowTitle}>{t.t(apkTitle)}</span>
            <span className={styles.value}>{apkHint}</span>
          </a>
        ) : (
          <p className={styles.hint}>{t.t('settings.noApk')}</p>
        )}
      </div>
    </section>
  );
}

/** Every board setting in one place: create, rename, order, remove. The bar and the list only switch. */
function BoardsSection() {
  const t = useT();
  const boards = useOrderedBoards();
  const boardId = useSession((s) => s.boardId);
  const createBoard = useSession((s) => s.createBoard);
  const renameBoard = useSession((s) => s.renameBoard);
  const deleteBoard = useSession((s) => s.deleteBoard);
  const setSetting = useSettings((s) => s.set);
  const cards = useCards((s) => s.cards);
  const showToast = useUi((s) => s.showToast);
  const [name, setName] = useState('');
  const [confirming, setConfirming] = useState<string | null>(null);
  const counts = (id: string) => openCards(ofBoard(cards, id)).length;
  const fail = (e: unknown) => showToast(e instanceof Error ? e.message : String(e));
  const create = () => {
    const n = name.trim();
    if (!n) return;
    createBoard(n).then(() => setName(''), fail);
  };
  const move = (i: number, dir: -1 | 1) => {
    const ids = boards.map((b) => b.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j] as string, ids[i] as string];
    void setSetting('boardOrder', ids);
  };
  return (
    <section className={styles.group} id="boards">
      <div className={controls.label}>{t.t('settings.boards')}</div>
      <div className={styles.card}>
        {boards.map((b, i) =>
          confirming === b.id ? (
            <div key={b.id} className={controls.footer}>
              <span className={`${styles.confirmText} ${controls.footerLeft}`}>{t.t('boards.confirmRemoveOne', { name: b.name })}</span>
              <button type="button" className={`${controls.btn} ${controls.quiet}`} onClick={() => setConfirming(null)} autoFocus>
                {t.t('sheet.keep')}
              </button>
              <button type="button" className={`${controls.btn} ${controls.danger}`} onClick={() => deleteBoard(b.id).then(() => setConfirming(null), fail)}>
                {t.t('boards.remove')}
              </button>
            </div>
          ) : (
            <div key={b.id} className={styles.boardRow} data-board-row={b.id}>
              <span className={`${styles.onScreen}${b.id === boardId ? ` ${styles.onScreenOn}` : ''}`} title={b.id === boardId ? t.t('boards.onScreen') : undefined} aria-label={b.id === boardId ? t.t('boards.onScreen') : undefined} />
              <BoardName id={b.id} name={b.name} onRename={(n) => renameBoard(b.id, n).catch(fail)} label={t.t('boards.nameOf', { name: b.name })} />
              <button type="button" className={controls.square} disabled={i === 0} onClick={() => move(i, -1)} aria-label={t.t('boards.up', { name: b.name })} title={t.t('boards.up', { name: b.name })}>
                <ChevronUp size={18} />
              </button>
              <button type="button" className={controls.square} disabled={i === boards.length - 1} onClick={() => move(i, 1)} aria-label={t.t('boards.down', { name: b.name })} title={t.t('boards.down', { name: b.name })}>
                <ChevronDown size={18} />
              </button>
              <button
                type="button"
                className={`${controls.square} ${styles.removeBoard}`}
                disabled={boards.length === 1 || counts(b.id) > 0}
                onClick={() => setConfirming(b.id)}
                aria-label={t.t('boards.removeOne', { name: b.name })}
                title={counts(b.id) > 0 ? t.t('boards.notEmpty') : t.t('boards.removeOne', { name: b.name })}
              >
                <Cross size={16} />
              </button>
            </div>
          ),
        )}
        <div className={styles.boardRow}>
          <span className={styles.onScreen} />
          <input className={controls.field} value={name} placeholder={t.t('boards.namePlaceholder')} maxLength={40} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && create()} aria-label={t.t('boards.new')} />
          <button type="button" className={`${controls.btn} ${controls.primary} ${styles.addBoard}`} disabled={!name.trim()} onClick={create}>
            {t.t('boards.add')}
          </button>
        </div>
        <p className={styles.hint}>{t.t('settings.boardsHint')}</p>
      </div>
    </section>
  );
}

/** A board's name, edited in place and saved when the field is left. */
function BoardName({ id, name, label, onRename }: { id: string; name: string; label: string; onRename(n: string): Promise<unknown> }) {
  const [draft, setDraft] = useState(name);
  useEffect(() => setDraft(name), [name, id]);
  return (
    <input
      className={`${controls.field} ${styles.boardName}`}
      value={draft}
      maxLength={40}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const n = draft.trim();
        if (!n) setDraft(name);
        else if (n !== name) void onRename(n);
      }}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      aria-label={label}
    />
  );
}

function AccountSection() {
  const t = useT();
  const navigate = useNavigate();
  const user = useSession((s) => s.user);
  const logout = useSession((s) => s.logout);
  const setEmail = useSession((s) => s.setEmail);
  const showToast = useUi((s) => s.showToast);
  const pending = useSync((s) => s.pending);
  const [confirming, setConfirming] = useState(false);
  const [email, setEmailDraft] = useState(user?.email ?? '');
  const [emailError, setEmailError] = useState(false);
  if (!user) return null;
  const commitEmail = () => {
    const next = email.trim() || null;
    if (next === (user.email ?? null)) return;
    setEmail(next).then(
      () => setEmailError(false),
      () => {
        setEmailError(true);
        showToast(t.t('account.emailInvalid'));
      },
    );
  };
  const signOut = async () => {
    if (pending > 0 && !confirming) {
      setConfirming(true);
      return;
    }
    await logout();
    navigate('/login', { replace: true });
  };
  return (
    <section className={styles.group}>
      <div className={controls.label}>{t.t('account.title')}</div>
      <div className={styles.card}>
        <div className={styles.row}>
          <span className={styles.rowTitle}>{t.t('account.signedInAs', { username: user.username })}</span>
          <span className={styles.value}>{user.role === 'admin' ? t.t('admin.role.admin') : null}</span>
        </div>
        {user.role === 'admin' ? (
          <Link to="/admin" className={styles.linkbtn} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
            {t.t('nav.admin')}
          </Link>
        ) : null}
        <div className={styles.row}>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span className={styles.rowTitle}>{t.t('account.email')}</span>
            <span className={styles.hint} style={{ margin: 0 }}>
              {t.t('account.emailHint')}
            </span>
          </div>
          <input type="email" className={`${controls.field} ${styles.select}`} value={email} placeholder={t.t('account.emailPlaceholder')} onChange={(e) => setEmailDraft(e.target.value)} onBlur={commitEmail} aria-label={t.t('account.email')} aria-invalid={emailError} autoComplete="email" />
        </div>
        <Link to="/password" className={styles.linkbtn} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
          {t.t('account.changePassword')}
        </Link>
        {confirming ? (
          <div className={controls.footer}>
            <span className={`${styles.hint} ${controls.footerLeft}`} style={{ margin: 0 }}>
              {t.t('account.signOutPending', { n: pending })}
            </span>
            <button type="button" className={`${controls.btn} ${controls.quiet}`} onClick={() => setConfirming(false)}>
              {t.t('sheet.cancel')}
            </button>
            <button type="button" className={`${controls.btn} ${controls.danger}`} onClick={() => void signOut()}>
              {t.t('account.confirm')}
            </button>
          </div>
        ) : (
          <button type="button" className={styles.linkbtn} style={{ color: 'var(--danger)' }} onClick={() => void signOut()}>
            {t.t('account.signOut')}
          </button>
        )}
      </div>
    </section>
  );
}

function DevicesSection() {
  const t = useT();
  const now = useNow();
  const token = useSession((s) => s.token);
  const [devices, setDevices] = useState<DeviceInfo[] | null>(null);
  const [failed, setFailed] = useState(false);
  const load = async () => {
    if (!token) return;
    try {
      setDevices((await api.devices(token)).devices);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  if (!token) return null;
  const revoke = async (id: string) => {
    await api.revokeDevice(token, id);
    await load();
    void flush();
  };
  return (
    <section className={styles.group}>
      <div className={controls.label}>{t.t('devices.title')}</div>
      <div className={styles.card}>
        {failed || devices === null ? <p className={styles.hint}>{failed ? t.t('devices.unavailable') : t.t('common.loading')}</p> : null}
        {devices?.map((d) => (
          <div key={d.id} className={styles.row}>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span className={styles.rowTitle}>{d.name}</span>
              <span className={styles.hint} style={{ margin: 0 }}>
                {d.current ? t.t('devices.thisDevice') : d.lastSeenAt ? t.t('devices.lastSeen', { when: formatRelative(d.lastSeenAt, now, t.locale) }) : ''}
              </span>
            </div>
            {d.current ? null : (
              <button type="button" className={styles.linkbtn} style={{ color: 'var(--danger)' }} onClick={() => void revoke(d.id)}>
                {t.t('devices.revoke')}
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

interface LabelEditorProps {
  title: string;
  count: number;
  values: (string | null)[];
  placeholder(i: number): string;
  colour?(i: number): string;
  onChange(next: (string | null)[]): void;
}

function LabelEditor({ title, count, values, placeholder, colour, onChange }: LabelEditorProps) {
  const [drafts, setDrafts] = useState<string[]>(() => Array.from({ length: count }, (_, i) => values[i] ?? ''));
  useEffect(() => {
    setDrafts(Array.from({ length: count }, (_, i) => values[i] ?? ''));
  }, [values, count]);
  const commit = (i: number) => {
    const next = Array.from({ length: count }, (_, k) => (k === i ? drafts[i]?.trim() || null : (values[k] ?? null)));
    if (next[i] !== (values[i] ?? null)) onChange(next);
  };
  return (
    <div className={styles.labels}>
      <span className={styles.rowTitle}>{title}</span>
      <div className={styles.labelGrid}>
        {Array.from({ length: count }, (_, i) => (
          <label key={i} className={styles.labelField}>
            {colour ? <span className={styles.swatch} style={{ background: colour(i) }} /> : null}
            <input
              className={controls.field}
              value={drafts[i] ?? ''}
              placeholder={placeholder(i)}
              onChange={(e) => setDrafts((d) => d.map((v, k) => (k === i ? e.target.value : v)))}
              onBlur={() => commit(i)}
              aria-label={`${title} ${i + 1}`}
            />
          </label>
        ))}
      </div>
    </div>
  );
}
